import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Layout } from '@/components/layout';
import {
  CancelOrderModal,
  MessageBubble,
  OrderProposalComposer,
  TypingIndicator,
} from '@/components/domain';
import { Avatar, ConfirmDialog, Icon, Spinner, EmptyState } from '@/components/atoms';
import { useAuthStore, useChatsStore, useMessagesStore } from '@/store';
import { useBackButton, useHapticFeedback, useWsEventOfType } from '@/hooks';
import { chats as chatsApi } from '@/api';
import { acceptOrder, cancelOrder } from '@/api/orders';
import { toast } from '@/components/atoms/Toast';
import { ws } from '@/ws';
import type { Message } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

const TYPING_THROTTLE_MS = 2_000;
const TYPING_EXPIRE_MS = 4_000;
const TYPING_TICK_MS = 250;

export function ChatThreadPage(): JSX.Element {
  useBackButton();
  const haptic = useHapticFeedback();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const chatId = Number(id);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const me = useAuthStore((s) => s.user);
  const messages = useMessagesStore((s) => s.byChat[chatId] ?? []);
  const setMessages = useMessagesStore((s) => s.setMessages);
  const appendMessage = useMessagesStore((s) => s.appendMessage);
  const updateByNonce = useMessagesStore((s) => s.updateByNonce);
  const chat = useChatsStore((s) => s.chats.find((c) => c.id === chatId));
  const peerName = chat?.peer_first_name ?? '';
  const peerAvatar = chat?.peer_avatar_url ?? null;
  const peerPresence = chat?.peer_presence;
  const peerSlug = chat?.peer_slug ?? null;
  const activeOrder = chat?.active_order ?? null;
  // F-170 B2.11 — when this chat is bound to a specific order, expose a
  // quick-jump shortcut next to the peer header so the client can drop
  // into the full Order detail view (price, schedule, cancellation) in
  // one tap. Hidden for general (no-order) chats.
  const linkedOrderId = chat?.order_id ?? activeOrder?.id ?? null;
  const upsertChat = useChatsStore((s) => s.upsertChat);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [peerTypingAt, setPeerTypingAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [proposalOpen, setProposalOpen] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState<number | null>(null);
  const [acceptOrderId, setAcceptOrderId] = useState<number | null>(null);
  const [acceptBusy, setAcceptBusy] = useState(false);
  const isSpecialist = me?.role === 'specialist';
  const lastTypingSentRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!Number.isFinite(chatId)) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await chatsApi.fetchMessages(chatId);
        if (!cancelled) setMessages(chatId, [...data.results].reverse());
      } catch (err) {
        logger.warn('Messages fetch failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chatId, setMessages]);

  useWsEventOfType('chat.message', (evt) => {
    const payload = evt.payload as { chat_id: number; message: Message };
    if (payload.chat_id !== chatId) return;
    appendMessage(chatId, payload.message);
  });

  // Peer-typing consumer: ignore own typing, ignore other chats, just refresh
  // the timestamp; the render-side check vs `now` decides visibility.
  const handleTyping = useCallback(
    (evt: { payload: unknown }) => {
      const payload = evt.payload as {
        chat_id: number;
        typing_user_id: number;
      };
      if (payload.chat_id !== chatId) return;
      if (payload.typing_user_id === me?.id) return;
      setPeerTypingAt(Date.now());
    },
    [chatId, me?.id],
  );
  useWsEventOfType('chat.typing', handleTyping);

  // Lazily fetch the chat detail if the chats list hasn't been loaded yet
  // (deep-link from notification, refresh, etc.). We only need the peer name.
  useEffect(() => {
    if (!Number.isFinite(chatId) || peerName) return;
    let cancelled = false;
    (async () => {
      try {
        const chat = await chatsApi.fetchChat(chatId);
        if (!cancelled) upsertChat(chat);
      } catch (err) {
        logger.warn('Chat fetch failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chatId, peerName, upsertChat]);

  // Drives indicator expiration. Cheap interval — only ticks while mounted.
  useEffect(() => {
    if (peerTypingAt === 0) return;
    const id = setInterval(() => setNow(Date.now()), TYPING_TICK_MS);
    return () => clearInterval(id);
  }, [peerTypingAt]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  // F-180 A9 — push a text message and surface the result. WS path is
  // fire-and-forget (success is observed when the server echoes back
  // chat.message with the same client_nonce), REST path awaits the
  // response and replaces the optimistic bubble. Any throw flips the
  // optimistic bubble to `status: 'failed'` so the user sees the
  // delivery failure instead of an invisible black hole.
  const deliverMessage = useCallback(
    async (text: string, nonce: string): Promise<void> => {
      try {
        if (ws.getStatus() === 'open') {
          ws.send({
            type: 'chat.send',
            payload: { chat_id: chatId, kind: 'text', text, client_nonce: nonce },
          });
        } else {
          const saved = await chatsApi.sendMessage(chatId, {
            kind: 'text',
            text,
            client_nonce: nonce,
          });
          appendMessage(chatId, saved);
        }
      } catch (err) {
        logger.error('Send failed', err);
        haptic.notification('error');
        updateByNonce(chatId, nonce, { status: 'failed' });
        toast.error('Не вдалось надіслати. Натисни щоб повторити.');
      }
    },
    [chatId, appendMessage, haptic, updateByNonce],
  );

  const send = async (): Promise<void> => {
    const text = draft.trim();
    if (!text || !Number.isFinite(chatId)) return;
    haptic.impact('light');
    const nonce = `n-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic: Message = {
      id: -Date.now(),
      chat_id: chatId,
      sender_id: me?.id ?? -1,
      kind: 'text',
      text,
      is_read: false,
      created_at: new Date().toISOString(),
      client_nonce: nonce,
      status: 'pending',
    };
    appendMessage(chatId, optimistic);
    setDraft('');
    if (composerRef.current) composerRef.current.style.height = '';
    await deliverMessage(text, nonce);
  };

  const retryMessage = useCallback(
    async (msg: Message): Promise<void> => {
      if (!msg.client_nonce || !msg.text) return;
      updateByNonce(chatId, msg.client_nonce, { status: 'pending' });
      await deliverMessage(msg.text, msg.client_nonce);
    },
    [chatId, deliverMessage, updateByNonce],
  );

  const myId = me?.id ?? -1;
  const rendered = useMemo(() => messages, [messages]);
  const peerIsTyping =
    peerTypingAt > 0 && now - peerTypingAt < TYPING_EXPIRE_MS;

  // C4: friction-on-commit. Orders ≥ €20 surface a confirm dialog so the
  // client doesn't accidentally lock in a non-trivial commitment with a
  // single tap; smaller orders accept inline to keep the cheap path snappy.
  const ACCEPT_CONFIRM_THRESHOLD_EUR = 20;

  const findOrderInMessages = useCallback(
    (orderId: number) => {
      for (const m of messages) {
        if (m.order?.id === orderId) return m.order;
      }
      return null;
    },
    [messages],
  );

  const runAcceptOrder = useCallback(
    async (orderId: number): Promise<void> => {
      try {
        await acceptOrder(orderId);
        haptic.notification('success');
        // Backend emits chat.message (kind=order_status) over WS — list updates itself.
      } catch (err) {
        logger.warn('acceptOrder failed', err);
        toast.error('Не вдалось прийняти замовлення');
      }
    },
    [haptic],
  );

  const handleAcceptOrder = useCallback(
    (orderId: number) => {
      const order = findOrderInMessages(orderId);
      const price = Number(order?.price_eur ?? 0);
      if (Number.isFinite(price) && price >= ACCEPT_CONFIRM_THRESHOLD_EUR) {
        setAcceptOrderId(orderId);
        return;
      }
      void runAcceptOrder(orderId);
    },
    [findOrderInMessages, runAcceptOrder],
  );

  const acceptOrderDetails = useMemo(() => {
    if (acceptOrderId == null) return null;
    return findOrderInMessages(acceptOrderId);
  }, [acceptOrderId, findOrderInMessages]);

  const confirmAccept = useCallback(async () => {
    if (acceptOrderId == null) return;
    setAcceptBusy(true);
    try {
      await runAcceptOrder(acceptOrderId);
    } finally {
      setAcceptBusy(false);
      setAcceptOrderId(null);
    }
  }, [acceptOrderId, runAcceptOrder]);

  // C3: defer cancellation to the modal — store the orderId in state, the
  // modal renders the textarea and calls back into `submitCancel` on confirm.
  const handleDeclineOrder = useCallback((orderId: number) => {
    setCancelOrderId(orderId);
  }, []);

  const submitCancel = useCallback(
    async (reason: string): Promise<void> => {
      if (cancelOrderId == null) return;
      try {
        await cancelOrder(cancelOrderId, { reason });
        haptic.notification('warning');
      } catch (err) {
        logger.warn('cancelOrder failed', err);
        toast.error('Не вдалось відхилити замовлення');
      }
    },
    [cancelOrderId, haptic],
  );

  const cancelOrderTitle = useMemo(() => {
    if (cancelOrderId == null) return undefined;
    for (const m of messages) {
      if (m.order?.id === cancelOrderId) return m.order.title;
    }
    return undefined;
  }, [cancelOrderId, messages]);

  const publishTyping = useCallback(() => {
    if (!Number.isFinite(chatId)) return;
    if (ws.getStatus() !== 'open') return;
    const t = Date.now();
    if (t - lastTypingSentRef.current < TYPING_THROTTLE_MS) return;
    lastTypingSentRef.current = t;
    ws.send({ type: 'chat.typing', payload: { chat_id: chatId } });
  }, [chatId]);

  return (
    <Layout title="" hideHeader hideNav>
      <div className={styles.chatBanner}>
        <button
          type="button"
          className={styles.chatBack}
          onClick={() => navigate(-1)}
          aria-label="Назад"
        >
          <Icon name="x" size={20} />
        </button>
        <button
          type="button"
          className={styles.chatPeer}
          onClick={() => {
            if (peerSlug) navigate(`/u/${peerSlug}`);
          }}
          disabled={!peerSlug}
          aria-label={peerSlug ? `Профіль ${peerName}` : peerName}
        >
          <Avatar src={peerAvatar} name={peerName} size={40} presence={peerPresence} />
          <span className={styles.chatBannerText}>
            <span className={styles.chatBannerName}>{peerName || 'Чат'}</span>
            {activeOrder?.title ? (
              <span className={styles.chatBannerOrder}>
                <Icon name="calendar" size={12} />
                {activeOrder.title}
                {activeOrder.scheduled_at
                  ? ` · ${dayjs(activeOrder.scheduled_at).format('D MMM, HH:mm')}`
                  : ''}
              </span>
            ) : null}
          </span>
        </button>
        {linkedOrderId ? (
          <button
            type="button"
            className={styles.chatOrderJump}
            onClick={() => navigate(`/orders/${linkedOrderId}`)}
            aria-label="Переглянути замовлення"
            title="Переглянути замовлення"
          >
            <Icon name="file-text" size={18} />
            <span className={styles.chatOrderJumpLabel}>Замовлення</span>
          </button>
        ) : null}
      </div>
      <div className={styles.chatScroll} ref={scrollRef}>
        {loading ? (
          <Spinner center />
        ) : rendered.length === 0 ? (
          <EmptyState
            icon="cat"
            title="Поки тиша"
            message="Напиши перше повідомлення — потім гарантовано не пропустиш відповідь."
          />
        ) : (
          rendered.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              isMine={m.sender_id === myId}
              onAcceptOrder={handleAcceptOrder}
              onDeclineOrder={handleDeclineOrder}
              onRetry={retryMessage}
            />
          ))
        )}
      </div>
      {peerIsTyping && (
        <TypingIndicator name={peerName || 'Співрозмовник'} />
      )}
      <form
        className={styles.composer}
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        {isSpecialist && (
          <button
            type="button"
            className={styles.composerAction}
            onClick={() => setProposalOpen(true)}
            aria-label="Запропонувати замовлення"
            title="Запропонувати замовлення"
          >
            <Icon name="calendar" size={20} />
          </button>
        )}
        {/* F-190 QA B6: switched from <input> to auto-growing <textarea>
            — chat messages routinely span multiple lines, and the
            single-line input clipped long copy in a way that made
            users distrust whether the message survived. Enter submits;
            Shift+Enter inserts a newline (browser default). */}
        <textarea
          ref={composerRef}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (e.target.value) publishTyping();
            const ta = e.currentTarget;
            ta.style.height = 'auto';
            ta.style.height = `${Math.min(ta.scrollHeight, 144)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (draft.trim()) void send();
            }
          }}
          placeholder="Напиши повідомлення…"
          aria-label="Повідомлення"
          rows={1}
        />
        <button type="submit" disabled={!draft.trim()}>
          Надіслати
        </button>
      </form>
      {isSpecialist && (
        <OrderProposalComposer
          chatId={chatId}
          open={proposalOpen}
          onClose={() => setProposalOpen(false)}
        />
      )}
      <CancelOrderModal
        open={cancelOrderId != null}
        orderTitle={cancelOrderTitle}
        onClose={() => setCancelOrderId(null)}
        onSubmit={submitCancel}
      />
      <ConfirmDialog
        open={acceptOrderId != null && acceptOrderDetails != null}
        title="Прийняти замовлення?"
        body={
          acceptOrderDetails
            ? `Прийняти замовлення «${acceptOrderDetails.title}» на ${acceptOrderDetails.price_eur} €?`
            : null
        }
        confirmLabel="Так, прийняти"
        cancelLabel="Скасувати"
        tone="primary"
        busy={acceptBusy}
        onConfirm={() => void confirmAccept()}
        onCancel={() => setAcceptOrderId(null)}
      />
    </Layout>
  );
}
